import { useState } from "react";
import { ImageOff, Quote } from "lucide-react";

const badTldrPhrases = [
  "læs mere",
  "læs mere her",
  "laes mere",
  "laes mere her",
  "read more",
  "klik her",
  "se mere",
];

function cleanText(value) {
  return (value || "")
    .replace(/^TL;?DR:?\s*/i, "")
    .replace(/^NYHED\s*\/\/\s*OVERBLIK\s*[-–—:]\s*/i, "")
    .replace(/\s+/g, " ")
    .trim();
}

function isSplitElementMarker(value) {
  return /^-+\s*SPLIT ELEMENT\s*-+$/i.test(String(value || "").trim());
}

function isBadTldr(value) {
  const text = cleanText(value).toLowerCase().replace(/[.:-]+$/g, "");
  return text.length < 35 || badTldrPhrases.some((phrase) => text === phrase || text.includes(phrase));
}

function makeFallbackTldr(article) {
  const text = cleanText(article.neutral_content);
  const sentences = text.split(/(?<=[.!?])\s+/).filter(Boolean);
  const summary = sentences.slice(0, 2).join(" ") || text;
  return summary.length > 260 ? `${summary.slice(0, 257).replace(/\s+\S*$/, "")}...` : summary;
}

function getTldr(article) {
  const cleaned = cleanText(article.tldr);
  if (cleaned && !isBadTldr(cleaned)) return cleaned;
  return makeFallbackTldr(article);
}

function ArticleEmbed({ block }) {
  return (
    <aside className="my-6 border-l-2 border-emerald-500 bg-emerald-50/70 dark:bg-[#213548] dark:border-[#9DB2BF] px-4 py-4">
      <div className="flex items-start gap-3">
        <Quote className="mt-1 h-4 w-4 shrink-0 text-emerald-700 dark:text-[#DDE6ED]" aria-hidden="true" />
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-emerald-700 dark:text-[#DDE6ED] mb-2">
            Info fra artiklen
          </p>
          <p className="text-sm leading-relaxed text-gray-800 dark:text-[#DDE6ED] whitespace-pre-line">
            {block.text}
          </p>
        </div>
      </div>
    </aside>
  );
}

function ArticleImage({ article }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div
        className="mb-6 min-h-48 bg-gray-100 dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 flex flex-col items-center justify-center gap-3 px-6 text-center"
        role="img"
        aria-label={`Billedet til ${article.neutral_title} kunne ikke indlæses`}
        data-testid="article-image-error"
      >
        <ImageOff className="h-8 w-8 text-gray-400" aria-hidden="true" />
        <p className="text-[10px] tracking-[0.16em] uppercase font-mono text-gray-500 dark:text-gray-400">
          Billedet kunne ikke indlæses
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <img
        src={article.image_url}
        alt={article.neutral_title}
        className="w-full h-auto max-h-80 object-cover"
        loading="lazy"
        onError={() => setFailed(true)}
        data-testid="article-image"
      />
    </div>
  );
}

function LinkedParagraph({ block, articleId, index }) {
  const links = Array.isArray(block.links) ? block.links : [];
  const parts = [];
  let rest = block.text || "";

  links.forEach((link, linkIndex) => {
    const linkText = cleanText(link.text);
    if (!linkText || !link.url) return;

    const position = rest.indexOf(linkText);
    if (position < 0) return;

    if (position > 0) parts.push(rest.slice(0, position));
    parts.push(
      <a
        key={`${articleId}-link-${index}-${linkIndex}`}
        href={link.url}
        target="_blank"
        rel="noreferrer"
        className="underline underline-offset-2 decoration-gray-400 hover:decoration-[#0A0A0A] dark:decoration-gray-400 dark:hover:decoration-zinc-100"
      >
        {linkText}
      </a>
    );
    rest = rest.slice(position + linkText.length);
  });

  if (rest) parts.push(rest);

  return (
    <p className="text-base leading-relaxed text-gray-800 dark:text-gray-200">
      {parts.length ? parts : block.text}
    </p>
  );
}

export function ArticleBody({ article }) {
  const bodyText = article.neutral_content || article.tldr || "";
  const paragraphs = bodyText
    .split("\n")
    .map((paragraph, index) => index === 0 ? cleanText(paragraph) : paragraph.trim())
    .filter((paragraph) => paragraph && !isSplitElementMarker(paragraph));
  const tldrText = getTldr(article);
  const hideEmbeds = article.source === "TV 2";
  const contentBlocks = Array.isArray(article.content_blocks)
    ? article.content_blocks.filter((block) => block.text && !(hideEmbeds && block.type === "embed"))
    : [];
  const linkedParagraphs = contentBlocks.filter((block) => block.type === "paragraph" && block.links?.length);
  const embeds = contentBlocks.filter((block) => block.type === "embed");
  const linkedBlockForParagraph = (paragraph) => linkedParagraphs.find((block) => cleanText(block.text) === cleanText(paragraph));

  return (
    <>
      {article.image_url && (
        <ArticleImage
          key={`${article.id}:${article.image_url}`}
          article={article}
        />
      )}

      {tldrText && (
        <div
          className="mb-8 px-4 py-3 border-l-2 border-[#0A0A0A] dark:border-zinc-200 bg-[#FAFAFA] dark:bg-zinc-900"
          data-testid="article-tldr"
        >
          <p className="text-[10px] tracking-[0.2em] uppercase font-mono text-gray-500 dark:text-gray-400 mb-1">
            TL;DR
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            {tldrText}
          </p>
        </div>
      )}

      <div className="space-y-4 mb-12">
        {embeds
          .filter((block) => (block.source_after_paragraph || 0) <= 0)
          .map((block, embedIndex) => (
            <ArticleEmbed key={`${article.id}-embed-start-${embedIndex}`} block={block} />
          ))}

        {paragraphs.map((p, i) => {
          const linkedBlock = linkedBlockForParagraph(p);
          return (
            <div key={`${article.id}-group-${i}`}>
              {linkedBlock ? (
                <LinkedParagraph
                  block={linkedBlock}
                  articleId={article.id}
                  index={i}
                />
              ) : (
                <p className="text-base leading-relaxed text-gray-800 dark:text-gray-200">
                  {p}
                </p>
              )}
              {embeds
                .filter((block) => (block.source_after_paragraph || 0) === i + 1)
                .map((block, embedIndex) => (
                  <ArticleEmbed key={`${article.id}-embed-${i}-${embedIndex}`} block={block} />
                ))}
            </div>
          );
        })}

        {embeds
          .filter((block) => (block.source_after_paragraph || 0) > paragraphs.length)
          .map((block, embedIndex) => (
            <ArticleEmbed key={`${article.id}-embed-end-${embedIndex}`} block={block} />
          ))}
      </div>
    </>
  );
}
